import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { ModalActionsComponent, PasslyCheckbox } from '@app/components';
import { FormControl  } from '@material-ui/core';
import { parseTimeSpan, toTimeSpan, onPressEnterModal, genericErrorString } from '@app/helpers';
import { organizationsService } from '@app/services';
import { InputNumber } from '@itglue/kaseya-ui';

export interface IReceivedData {
    orgId: string;
    lockAfterAttempts: number;
    lockoutDuration: string;
    resetRequiresVerification: boolean;
    resetByEmail: boolean;
    resetBySms: boolean;
    requiresComplexity: boolean;
    minimumPasswordAge: string;
    maximumPasswordAge: string;
    minimumPasswordLength: number;
    handleRefresh: Function;
}

export const EditOrgPasswordPolicyDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [lockAfterAttempts, setLockAfterAttempts] = useState(0);
    const [lockAfterAttemptsInvalid, setLockAfterAttemptsInvalid] = useState(false);
    const [lockoutDuration, setLockoutDuration] = useState(0);
    const [lockoutDurationInvalid, setLockoutDurationInvalid] = useState(false);
    const [resetRequiresVerification, setResetRequiresVerification] = useState(false);
    const [resetByEmail, setResetByEmail] = useState(false);
    const [resetBySms, setResetBySms] = useState(false);
    const [requiresComplexity, setRequiresComplexity] = useState(false);
    const [minimumPasswordAge, setMinimumPasswordAge] = useState(0);
    const [minimumPasswordAgeInvalid, setMinimumPasswordAgeInvalid] = useState(false);
    const [maximumPasswordAge, setMaximumPasswordAge] = useState(0);
    const [maximumPasswordAgeInvalid, setMaximumPasswordAgeInvalid] = useState(false);
    const [minimumPasswordLength, setMinimumPasswordLength] = useState(0);
    const [minimumPasswordLengthInvalid, setMinimumPasswordLengthInvalid] = useState(false);
    const [formValid, setFormValid] = useState(true);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        setLockAfterAttempts(props.lockAfterAttempts);

        let formatted: number = parseTimeSpan(props.lockoutDuration, 'minutes');
        setLockoutDuration(formatted);

        setResetRequiresVerification(props.resetRequiresVerification);
        setResetByEmail(props.resetByEmail);
        setResetBySms(props.resetBySms);
        setRequiresComplexity(props.requiresComplexity);

        formatted = parseTimeSpan(props.minimumPasswordAge, 'days');
        setMinimumPasswordAge(formatted);

        formatted = parseTimeSpan(props.maximumPasswordAge, 'days');
        setMaximumPasswordAge(formatted);

        setMinimumPasswordLength(props.minimumPasswordLength);
    }, []);

    useEffect(() => {
        if (lockAfterAttemptsInvalid || lockoutDurationInvalid) {
            setFormValid(false);
        }
        else {
            setFormValid(true);
        }
    }, [lockAfterAttemptsInvalid, lockoutDurationInvalid]);

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'lockAfterAttempts':
                setLockAfterAttempts(value);
                break;
            case 'lockoutDuration':
                setLockoutDuration(value);
                break;
            case 'minimumPasswordAge':
                setMinimumPasswordAge(value);
                break;
            case 'maximumPasswordAge':
                setMaximumPasswordAge(value);
                break;
            case 'minimumPasswordLength':
                setMinimumPasswordLength(value);
                break;
            default:
                break;
        }
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        switch (name) {
            case 'resetRequiresVerification':
                setResetRequiresVerification(checked);
                break;
            case 'resetByEmail':
                setResetByEmail(checked);
                break;
            case 'resetBySms':
                setResetBySms(checked);
                break;
            case 'requiresComplexity':
                setRequiresComplexity(checked);
                break;
            default:
                break;
        }
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));

        dispatch(getLoaderBtn(false));
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSuccess = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        props.handleRefresh(true);
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        let jsonObject: any = {};
        jsonObject.policy = {};
        jsonObject.policy.LockAfterAttempts = lockAfterAttempts;
        jsonObject.policy.LockoutDuration = toTimeSpan(lockoutDuration, 'm');
        jsonObject.policy.ResetRequiresVerification = resetRequiresVerification;
        jsonObject.policy.ResetByEmail = resetByEmail;
        jsonObject.policy.ResetBySms = resetBySms;
        jsonObject.policy.RequiresComplexity = requiresComplexity;
        jsonObject.policy.MinimumPasswordAgeTicks = toTimeSpan(minimumPasswordAge, 'ticks');
        jsonObject.policy.MaximumPasswordAgeTicks = toTimeSpan(maximumPasswordAge, 'ticks');
        jsonObject.policy.MinimumPasswordLength = minimumPasswordLength;

        organizationsService.editOrgPasswordPolicy(props.orgId, jsonObject, handleError, handleSuccess);
    };

    const onLockAfterAttemptsChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const lockAfterAttpemtsChosen = e.target.value;

        if (lockAfterAttpemtsChosen === '' || lockAfterAttpemtsChosen < 0) {
            setLockAfterAttemptsInvalid(true);
        }
        else {
            setLockAfterAttemptsInvalid(false);
        }
    };

    const onLockoutDurationChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const lockDurationChosen = e.target.value;

        if (lockDurationChosen === '' || lockDurationChosen < 0) {
            setLockoutDurationInvalid(true);
        }
        else {
            setLockoutDurationInvalid(false);
        }
    };

    const onMinimumPasswordAgeChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const minimumPasswordAgeChosen = e.target.value;

        if (minimumPasswordAgeChosen === '' || minimumPasswordAgeChosen < 0) {
            setMinimumPasswordAgeInvalid(true);
        }
        else {
            setMinimumPasswordAgeInvalid(false);
        }
    };

    const onMaximumPasswordAgeChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const maximumPasswordAgeChosen = e.target.value;

        if (maximumPasswordAgeChosen === '' || maximumPasswordAgeChosen < 1) {
            setMaximumPasswordAgeInvalid(true);
        }
        else {
            setMaximumPasswordAgeInvalid(false);
        }
    };

    const onMinimumPasswordLengthChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const minimumPasswordLengthChosen = e.target.value;

        if (minimumPasswordLengthChosen === '' || minimumPasswordLengthChosen < 3) {
            setMinimumPasswordLengthInvalid(true);
        }
        else {
            setMinimumPasswordLengthInvalid(false);
        }
    };

    return (
        <>
                <form className='modal__form modal__form-edit-org-password' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid)}>
                    <div className='modal__form-edit-org-password--wrapper'>
                        <h3>{ translation('LOCKOUT_POLICY') }</h3>
                        <FormControl>
                            <InputNumber
                                id='lockAfterAttempts-input'
                                label={`${translation('LOCK_AFTER_ATTEMPTS')} (${translation('LOCK_ATTEMPTS_PLURAL')})`}
                                name='lockAfterAttempts'
                                value={lockAfterAttempts}
                                onValueChange={onLockAfterAttemptsChange}
                                showButtons
                                idecrementButtonClassName={''}
                                min={0}
                                max={9999}
                                errorMessage={lockAfterAttemptsInvalid ? translation('REQUIRED_FIELD') : undefined}
                                required
                            />
                        </FormControl>
                        <FormControl>
                            <InputNumber
                                id='lockoutDuration-input'
                                label={`${translation('LOCKOUT_DURATION')} (${translation('LOCKOUT_TIME')})`}
                                name='lockoutDuration'
                                value={lockoutDuration}
                                onValueChange={onLockoutDurationChange}
                                showButtons
                                idecrementButtonClassName={''}
                                min={0}
                                max={9999}
                                errorMessage={lockoutDurationInvalid ? translation('REQUIRED_FIELD') : undefined}
                                required
                            />
                        </FormControl>
                    </div>
                    <div className='modal__form-edit-org-password--wrapper'>
                        <h3>{translation('RESET_POLICY')}</h3>
                        <PasslyCheckbox
                            name='resetRequiresVerification'
                            checked={resetRequiresVerification}
                            onChange={onCheckboxChange}
                            label={translation('REQUIRE_RESET_CODE')}
                        />
                        <PasslyCheckbox
                            name='resetByEmail'
                            checked={resetByEmail}
                            onChange={onCheckboxChange}
                            label={translation('RESET_BY_EMAIL')}
                        />
                        <PasslyCheckbox
                            name='resetBySms'
                            checked={resetBySms}
                            onChange={onCheckboxChange}
                            label={translation('RESET_BY_SMS')}
                        />
                    </div>
                    <div className='modal__form-edit-org-password--wrapper'>
                        <h3>{translation('PASSWORD_STRENGTH_POLICY')}</h3>
                        <PasslyCheckbox
                            name='requiresComplexity'
                            checked={requiresComplexity}
                            onChange={onCheckboxChange}
                            label={translation('PASS_REQUIRES_COMPLEX')}
                        />

                        <FormControl>
                            <InputNumber
                                id='minimumPasswordAge-input'
                                label={`${translation('AD_PASS_MIN_AGE')} (${translation('DAYS')})`}
                                name='minimumPasswordAge'
                                value={minimumPasswordAge}
                                onValueChange={onMinimumPasswordAgeChange}
                                showButtons
                                idecrementButtonClassName={''}
                                min={0}
                                max={9999}
                                errorMessage={minimumPasswordAgeInvalid ? translation('REQUIRED_FIELD') : undefined}
                                required
                            />
                        </FormControl>
                        <FormControl>
                            <InputNumber
                                id='maximumPasswordAge-input'
                                label={`${translation('AD_PASS_MAX_AGE')} (${translation('DAYS')})`}
                                name='maximumPasswordAge'
                                value={maximumPasswordAge}
                                onValueChange={onMaximumPasswordAgeChange}
                                showButtons
                                idecrementButtonClassName={''}
                                min={0}
                                max={10675199}
                                errorMessage={maximumPasswordAgeInvalid ? translation('REQUIRED_FIELD') : undefined}
                                required
                            />
                        </FormControl>
                        <FormControl>
                            <InputNumber
                                id='minimumPasswordLength-input'
                                label={`${translation('AD_PASS_MIN_LENGTH')} (${translation('CHARACTERS')})`}
                                name='minimumPasswordLength'
                                value={minimumPasswordLength}
                                onValueChange={onMinimumPasswordLengthChange}
                                showButtons
                                idecrementButtonClassName={''}
                                min={3}
                                max={9999}
                                errorMessage={minimumPasswordLengthInvalid ? translation('REQUIRED_FIELD') : undefined}
                                required
                            />
                        </FormControl>
                    </div>
                </form>
            {
                errorMessage &&
                <div className='modal__error'>{errorMessage}</div>
            }
            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={!formValid}
                id='editOrgPolicy'
            />
        </>
    );
};